import {
  useParams,
  useNavigate,
  generatePath,
  Link as RouterLink,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Link,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GavelIcon from "@mui/icons-material/Gavel";
import { getAdminOrderDetail } from "../../../utility/httpActions/adminActions";
import { frontendRoutes } from "../../../utility/frontendRoutes";
import OrderChangeLog from "./OrderChangeLog";
import OrderDeliveryTracking from "./OrderDeliveryTracking";
import ProofImages from "./ProofImages";
import OrderPriceNegotiation from "./OrderPriceNegotiation";
import OrderCancellation from "./OrderCancellation";
import OrderHandoverAttempts from "./OrderHandoverAttempts";

const formatCurrency = (cents, currency) => {
  const amount = ((cents || 0) / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${currency} ${amount}` : amount;
};

const formatDateTime = (date) =>
  date
    ? new Date(date).toLocaleString(undefined, { timeZoneName: "short" })
    : "—";

const accountStatusColor = (status) =>
  status === "ACTIVE"
    ? "success"
    : status === "SUSPENDED"
      ? "warning"
      : "error";

const statusColor = {
  PENDING: "default",
  IN_PROGRESS: "info",
  REJECTED: "error",
  IN_TRANSIT: "info",
  READY_FOR_PICKUP: "info",
  DELIVERED: "primary",
  FULFILLED: "success",
  CANCELLED: "default",
  RETURNED: "warning",
  REFUNDED: "warning",
  DISPUTED: "error",
};

const OrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const {
    data: order,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["admin-order", orderId],
    queryFn: () => getAdminOrderDetail(orderId),
  });

  if (isLoading) return <CircularProgress />;
  if (error || !order) return <Alert severity="error">Order not found.</Alert>;

  const seller = order.shop?.createdBy;
  const isDisputed = order.status === "DISPUTED" || !!order.dispute?.reason;

  const timelineSteps = (
    order.statusHistory?.length
      ? order.statusHistory
      : [{ status: order.status, at: order.createdAt }]
  ).map(({ status, at }) => ({ label: status, date: at }));

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(frontendRoutes.ADMIN_ORDERS)}
        sx={{ mb: 2 }}
      >
        Back to Orders
      </Button>

      <Typography variant="h5" fontWeight={700} mb={1}>
        Order #{order.orderNumber}
      </Typography>
      <Chip
        label={order.status}
        color={statusColor[order.status] || "default"}
        sx={{ mb: 3 }}
      />

      {isDisputed && (
        <Alert
          severity="warning"
          icon={<GavelIcon />}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() =>
                navigate(
                  generatePath(frontendRoutes.ADMIN_DISPUTE_DETAIL, {
                    orderId: order._id,
                  }),
                )
              }
            >
              View Dispute
            </Button>
          }
          sx={{ mb: 2 }}
        >
          This order has a dispute — resolve it from the Dispute detail page.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left column */}
        <Grid item xs={12} md={7}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={1}>
                Order Summary
              </Typography>
              <Typography variant="body2">
                Placed: {formatDateTime(order.createdAt)}
              </Typography>
              <Typography variant="body2">
                Subtotal: {formatCurrency(order.subtotalCents, order.currency)}
              </Typography>
              <Typography variant="body2">
                Delivery fee:{" "}
                {formatCurrency(order.deliveryFeeCents || 0, order.currency)}
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                Total: {formatCurrency(order.totalCents, order.currency)}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">
                Delivery method: {order.delivery?.deliveryMethod || "—"}
              </Typography>
              {order.delivery?.addressSnapshot && (
                <Typography variant="body2">
                  Address:{" "}
                  {[
                    order.delivery.addressSnapshot.addressLine1,
                    order.delivery.addressSnapshot.city,
                    order.delivery.addressSnapshot.state,
                    order.delivery.addressSnapshot.country,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </Typography>
              )}
              {order.shopAddressSnapshot && (
                <Typography variant="body2">
                  Pickup address:{" "}
                  {[
                    order.shopAddressSnapshot.addressLine1,
                    order.shopAddressSnapshot.city,
                    order.shopAddressSnapshot.state,
                    order.shopAddressSnapshot.country,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </Typography>
              )}
              <Typography variant="body2">
                Payment method: {order.payment?.paymentMethod || "—"}
              </Typography>
              <Typography variant="body2">
                Payment timing: {order.payment?.paymentTiming || "—"}
              </Typography>
              <Typography variant="body2">
                Payment status:{" "}
                <Chip
                  label={order.payment?.paymentStatus || "—"}
                  size="small"
                />
              </Typography>
              {order.payment?.paymentInstructionsSnapshot && (
                <Box mt={1} p={1} bgcolor="grey.100" borderRadius={1}>
                  <Typography variant="caption" fontFamily="monospace">
                    {order.payment.paymentInstructionsSnapshot}
                  </Typography>
                </Box>
              )}
              {/* {order.pickupOtp && (
                <Typography variant="body2" mt={1}>Pickup OTP: <strong>{order.pickupOtp}</strong></Typography>
              )} */}
              {order.customerNote && (
                <Typography variant="body2" mt={1}>
                  Buyer note: {order.customerNote}
                </Typography>
              )}
              {order.sellerNote && (
                <Typography variant="body2" mt={1}>
                  Seller note: {order.sellerNote}
                </Typography>
              )}
              {order.rejectionReason && (
                <Typography variant="body2" mt={1} color="error.main">
                  Rejection reason: {order.rejectionReason}
                </Typography>
              )}
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2" fontWeight={600} mb={0.5}>
                Items
              </Typography>
              {order.items?.map((item, i) => (
                <Box key={i} mb={item.note ? 0.5 : 0}>
                  <Typography variant="body2">
                    {item.product?._id ? (
                      <Link
                        component={RouterLink}
                        to={generatePath(frontendRoutes.ADMIN_PRODUCT_DETAIL, {
                          productId: item.product._id,
                        })}
                        underline="hover"
                      >
                        {item.productName}
                      </Link>
                    ) : (
                      item.productName
                    )}{" "}
                    × {item.quantity} —{" "}
                    {formatCurrency(item.lineTotalCents, item.currency)}
                    {item.product?.status &&
                      item.product.status !== "PUBLISHED" && (
                        <Chip
                          label={item.product.status}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                  </Typography>
                  {item.note && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ pl: 1 }}
                    >
                      Note: {item.note}
                    </Typography>
                  )}
                </Box>
              ))}
            </CardContent>
          </Card>

          <OrderPriceNegotiation order={order} />

          <OrderCancellation order={order} />

          <OrderHandoverAttempts order={order} />

          <OrderDeliveryTracking order={order} />

          <OrderChangeLog order={order} />

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={1}>
                Payment Evidence
              </Typography>
              <ProofImages
                title="Buyer's Payment Proof"
                images={order.payment?.buyerPaymentProofImages}
              />
              <ProofImages
                title="Seller's Payment Proof"
                images={order.payment?.sellerPaymentProofImages}
              />
              {!order.payment?.buyerPaymentProofImages?.length &&
                !order.payment?.sellerPaymentProofImages?.length && (
                  <Typography variant="body2" color="text.secondary">
                    No proof images uploaded.
                  </Typography>
                )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right column */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={0.5}>
                Buyer
              </Typography>
              {order.user?._id ? (
                <Link
                  component={RouterLink}
                  to={generatePath(frontendRoutes.ADMIN_USER_DETAIL, {
                    userId: order.user._id,
                  })}
                  underline="hover"
                  variant="body2"
                  sx={{ display: "block" }}
                >
                  {order.user?.name || "—"}
                </Link>
              ) : (
                <Typography variant="body2">
                  {order.user?.name || "—"}
                </Typography>
              )}
              <Typography variant="body2">
                {order.user?.email || order.user?.phoneNumber || "—"}
              </Typography>
              <Chip
                label={order.user?.accountStatus || "ACTIVE"}
                color={accountStatusColor(order.user?.accountStatus)}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={0.5}>
                Seller / Shop
              </Typography>
              {order.shop?._id ? (
                <Link
                  component={RouterLink}
                  to={generatePath(frontendRoutes.ADMIN_SHOP_DETAIL, {
                    shopId: order.shop._id,
                  })}
                  underline="hover"
                  variant="body2"
                  sx={{ display: "block" }}
                >
                  {order.shop?.shopName || "—"}
                </Link>
              ) : (
                <Typography variant="body2">
                  {order.shop?.shopName || "—"}
                </Typography>
              )}
              {order.shop?.status && order.shop.status !== "ACTIVE" && (
                <Chip
                  label={order.shop.status}
                  color="warning"
                  size="small"
                  sx={{ mt: 0.5, mb: 0.5 }}
                />
              )}
              {seller && (
                <>
                  {seller._id ? (
                    <Link
                      component={RouterLink}
                      to={generatePath(frontendRoutes.ADMIN_USER_DETAIL, {
                        userId: seller._id,
                      })}
                      underline="hover"
                      variant="body2"
                      sx={{ display: "block" }}
                    >
                      {seller.name}
                    </Link>
                  ) : (
                    <Typography variant="body2">{seller.name}</Typography>
                  )}
                  <Typography variant="body2">
                    {seller.email || seller.phoneNumber || "—"}
                  </Typography>
                  <Chip
                    label={seller.accountStatus || "ACTIVE"}
                    color={accountStatusColor(seller.accountStatus)}
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography fontWeight={600} mb={1}>
                Timeline
              </Typography>
              <Stepper
                orientation="vertical"
                activeStep={timelineSteps.length - 1}
              >
                {timelineSteps.map(({ label, date }, i) => (
                  <Step key={i} completed>
                    <StepLabel>
                      <Typography variant="body2">
                        {label.replace(/_/g, " ")}
                      </Typography>
                      {date && (
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(date)}
                        </Typography>
                      )}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetail;
